import { useMemo } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import { ActionIcon, Box, ColorSwatch, Menu, Text, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { openConfirmModal } from "@mantine/modals";
import { IconDots } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  createMRTColumnHelper,
  MantineReactTable,
  MRT_ColumnDef,
  useMantineReactTable,
} from "mantine-react-table";

import {
  getGetDepartmentsOptionallyfilteredbylocationQueryKey,
  useDeleteDepartment,
  useGetDepartmentsOptionallyfilteredbylocation,
} from "@/shared/api/generated/departments";
import { GetDepartmentData } from "@/shared/api/generated/schemas";
import { getDefaultMRTOptions } from "@/shared/components/table/defaultMRTOptions";
import { Can } from "@/shared/providers/casl-provider";
import { useAppAbility } from "@/shared/providers/casl-provider/use-app-ability";

import UpdateDepartmentModal from "./update-department/update-department-modal";

export default function DepartmentsTable({ museumId }: { museumId?: string }) {
  const { t } = useLingui();

  const { data } = useGetDepartmentsOptionallyfilteredbylocation({
    location: museumId ?? undefined,
  });

  const ability = useAppAbility();
  const canDoAnyActions =
    ability.can("update", "Department") || ability.can("delete", "Department");

  const ch = createMRTColumnHelper<GetDepartmentData>();

  const columns = [
    ch.accessor("name", { header: t`Navn` }),
    ch.display({
      id: "calendarColor",
      header: t`Farge`,
      Cell: ({ row }) => (
        <ColorSwatch
          color={row.original.calendarColor ?? ""}
          size={20}
          style={{ "--cs-radius": "var(--mantine-radius-sm)" }}
        />
      ),
      size: 80,
    }),
    ...(canDoAnyActions
      ? [
          ch.display({
            id: "actions",
            header: "",
            Cell: DepartmentsActions,
            enableColumnActions: false,
            size: 70,
          }),
        ]
      : []),
  ];

  const table = useMantineReactTable({
    columns,
    data: useMemo(() => data?.data ?? [], [data?.data]),
    ...getDefaultMRTOptions<GetDepartmentData>(),
  });

  return <MantineReactTable table={table} />;
}

const DepartmentsActions: MRT_ColumnDef<GetDepartmentData>["Cell"] = ({
  row,
}) => {
  const [isUpdateModalOpen, updateModal] = useDisclosure(false);

  return (
    <Box style={{ textAlign: "right" }}>
      <Menu position="bottom-end">
        <Menu.Target>
          <ActionIcon variant="subtle" color="gray">
            <IconDots />
          </ActionIcon>
        </Menu.Target>

        <Menu.Dropdown>
          <Can I="update" a="Department">
            <Menu.Item onClick={() => updateModal.open()}>
              <Trans>Endre</Trans>
            </Menu.Item>
          </Can>
          <Can I="delete" a="Department">
            <DeleteMenuItem id={row.original.id} name={row.original.name} />
          </Can>
        </Menu.Dropdown>
      </Menu>

      <UpdateDepartmentModal
        department={row.original}
        opened={isUpdateModalOpen}
        close={updateModal.close}
      />
    </Box>
  );
};

function DeleteMenuItem({ id, name }: { id: string; name: string }) {
  const { t } = useLingui();
  const queryClient = useQueryClient();

  const { mutateAsync: deleteDepartment } = useDeleteDepartment({
    mutation: {
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: getGetDepartmentsOptionallyfilteredbylocationQueryKey(),
        }),
    },
  });

  const openModal = () =>
    openConfirmModal({
      title: (
        <Title component="span" size="sm">
          <Trans>Slett avdeling</Trans>
        </Title>
      ),
      children: (
        <Text size="sm">
          <Trans>
            Er du sikker på at du vil fjerne <b>{name}</b> avdelingen?
          </Trans>
        </Text>
      ),
      labels: { confirm: t`Slett`, cancel: t`Avbryt` },
      cancelProps: { variant: "subtle" },
      confirmProps: { color: "red" },
      onConfirm: () => deleteDepartment({ id }),
    });

  return (
    <Menu.Item color="red" onClick={() => openModal()}>
      <Trans>Slett</Trans>
    </Menu.Item>
  );
}
